import momentJalaali from 'moment-jalaali'

import { getTranslate, lang } from '../localization'

const ISO_SESSION_TIME =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/
const LEGACY_SESSION_TIME = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/

function isValidISOSessionTime(value: string): boolean {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/)
  if (!match) {
    return false
  }

  const [, year, month, day, hour, minute, second] = match.map(Number)
  const calendarDate = new Date(Date.UTC(year, month - 1, day))

  return (
    calendarDate.getUTCFullYear() === year &&
    calendarDate.getUTCMonth() === month - 1 &&
    calendarDate.getUTCDate() === day &&
    hour <= 23 &&
    minute <= 59 &&
    second <= 59
  )
}

export type DateFullObject = {
  date: string
  month: string
  shortMonth: string
  monthDay: number
  weekDay: string
  year: string
}

export type DateItem = {
  gregorianDate: DateFullObject
  jalaaliDate: DateFullObject
}

export function getDatesList(
  perPage: number,
  from?: string,
  mode: 'day' | 'month' | '10-days' = 'day'
): DateItem[] {
  // If mode is 'month' or '10-days', start at the beginning of the month; otherwise, start at the specific date
  const startDate =
    mode === 'day' ? momentJalaali(from) : momentJalaali(from).startOf('month')
  const datesList: DateItem[] = []

  if (mode === 'month' && perPage === 12) {
    // Yearly view: Generate 12 months, each starting on the first day of the month
    for (let i = 0; i < perPage; i += 1) {
      const tmp = startDate.clone().add(i, 'months').startOf('month') // Ensure first day of each month
      datesList.push({
        gregorianDate: {
          date: tmp.format('YYYY-MM-DD'),
          month: tmp.format('MMMM'),
          shortMonth: tmp.format('MMM'),
          monthDay: tmp.date(),
          weekDay: tmp.format('ddd'),
          year: tmp.format('YYYY')
        },
        jalaaliDate: {
          date: tmp.format('jYYYY-jMM-jDD'),
          month: tmp.format('jMMMM'),
          shortMonth: tmp.format('jMMM'),
          monthDay: Number(tmp.format('jD')),
          weekDay: tmp.format('ddd'),
          year: tmp.format('jYYYY')
        }
      })
    }
  } else if (mode === '10-days' && perPage === 9) {
    // Three-Month View: Generate 10-day segments starting from the first day of each month
    for (let monthIndex = 0; monthIndex < 3; monthIndex += 1) {
      const monthStart = startDate
        .clone()
        .add(monthIndex, 'months')
        .startOf('month') // Start of each month
      for (let i = 0; i < 3; i += 1) {
        const tmp = monthStart.clone().add(i * 10, 'days') // Each 10-day segment
        datesList.push({
          gregorianDate: {
            date: tmp.format('YYYY-MM-DD'),
            month: tmp.format('MMMM'),
            shortMonth: tmp.format('MMM'),
            monthDay: tmp.date(),
            weekDay: tmp.format('ddd'),
            year: tmp.format('YYYY')
          },
          jalaaliDate: {
            date: tmp.format('jYYYY-jMM-jDD'),
            month: tmp.format('jMMMM'),
            shortMonth: tmp.format('jMMM'),
            monthDay: Number(tmp.format('jD')),
            weekDay: tmp.format('ddd'),
            year: tmp.format('jYYYY')
          }
        })
      }
    }
  } else {
    // Default Daily View (Monthly): Generate per day for a total of `perPage` days, starting on the exact selected date
    for (let i = 0; i < perPage; i += 1) {
      const tmp = startDate.clone().add(i, 'days')
      datesList.push({
        gregorianDate: {
          date: tmp.format('YYYY-MM-DD'),
          month: tmp.format('MMMM'),
          shortMonth: tmp.format('MMM'),
          monthDay: tmp.date(),
          weekDay: tmp.format('ddd'),
          year: tmp.format('YYYY')
        },
        jalaaliDate: {
          date: tmp.format('jYYYY-jMM-jDD'),
          month: tmp.format('jMMMM'),
          shortMonth: tmp.format('jMMM'),
          monthDay: Number(tmp.format('jD')),
          weekDay: tmp.format('ddd'),
          year: tmp.format('jYYYY')
        }
      })
    }
  }

  return datesList
}
export type DatePickerType = 'gregorian' | 'jalaali' | undefined

export type DatePickerSizeType = 'big' | 'small'

export type DateRangeType = 'END_DATE' | 'START_DATE'

export type DateObject = {
  gregorianStartDate?: string | undefined
  gregorianEndDate?: string | undefined
  jalaaliStartDate?: string | undefined
  jalaaliEndDate?: string | undefined
}

export function GregorianWithDetailsToGregorian(
  gregorianDateWithDetails: string
): any {
  return momentJalaali(gregorianDateWithDetails, 'YYYY-MM-DD HH:mm:ss').format(
    'YYYY-MM-DD'
  )
}

export function jalaaliToGregorian(jalaaliDate: string): any {
  return momentJalaali(jalaaliDate, 'jYYYY-jMM-jDD').format('YYYY-MM-DD')
}
export function jalaaliToGregorianWithHourMinuteAndSecond(
  jalaaliDateWithTime: string
): string {
  return momentJalaali(jalaaliDateWithTime, 'jYYYY-jMM-jDD HH:mm:ss').format(
    'YYYY-MM-DD HH:mm:ss'
  )
}

/**
 * True when a Jalali conversion produced no usable date.
 *
 * Go marshals its zero `time.Time` as `0001-01-01T00:00:00Z`, and services send that for
 * "has not happened yet" \u2014 a provider that has never fetched ARI, a webhook never attempted.
 * Converting year 1 CE to Jalali yields a negative year, which moment-jalaali renders as
 * `0NaN-NaN-NaN` while the TIME half still formats cleanly, so the result is the
 * distinctive `0NaN-NaN-NaN 00:00:00` an operator sees on screen.
 *
 * Checking the rendered output rather than `moment.isValid()` is deliberate: moment
 * considers year 1 a valid instant, so `isValid()` returns true and would not catch this.
 * Unparseable input takes the same path.
 */
const jalaaliRenderFailed = (formatted: string): boolean =>
  formatted.includes('NaN')

export function gregorianToJalaali(gregorianDate: string): string {
  if (!gregorianDate) {
    return ''
  }
  const formatted = momentJalaali(gregorianDate, 'YYYY-MM-DD').format(
    'jYYYY-jMM-jDD'
  )

  return jalaaliRenderFailed(formatted) ? '' : formatted
}

export function gregorianToJalaaliYear(gregorianDate: string): string {
  return momentJalaali(gregorianDate, 'YYYY').format('jYYYY')
}

/**
 * Turn a timestamp from the API into an instant.
 *
 * Countdowns need the instant, not a rendering: `new Date("2026-08-24 05:24:42")` parses an
 * unlabelled datetime as LOCAL time, so a Tehran wall time read in CEST lands 1h30m late and a
 * countdown built from it is inflated by exactly that. Observed 2026-08-24: a booking timer
 * showed 1:39:12 on a hold with roughly 9 minutes left, which is worse than a wrong clock --
 * the user trusts a window they do not have.
 *
 * Both shapes are accepted so the panel and the BFF need not deploy in lockstep:
 *
 *   "2026-08-24T01:54:42Z"   an instant -- parsed directly
 *   "2026-08-24 05:24:42"    legacy Tehran wall time -- pinned to +03:30
 *
 * Returns null rather than an Invalid Date, so callers must decide what to show instead of
 * rendering "NaN" or counting down from nonsense.
 */
export function parseServerInstant(value?: string | null): Date | null {
  if (!value) {
    return null
  }

  // Ranges are constrained in the pattern rather than left to the parser, because the two
  // available parsers are each lenient in a different place:
  //
  //   new Date('2026-02-30T01:54:42Z')  ->  2026-03-02, silently rolled over
  //   moment strict '...+25:00'         ->  accepted, despite no such offset existing
  //
  // A rolled-over date is the dangerous one: it yields a real, plausible instant that is
  // simply not the one that was sent, which is the whole failure mode this helper exists to
  // remove. Hours are 00-23, minutes and seconds 00-59, and an offset is likewise bounded.
  const isoShape =
    /^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d:[0-5]\d(\.\d+)?(Z|[+-]([01]\d|2[0-3]):[0-5]\d)$/
  const legacyShape =
    /^\d{4}-\d{2}-\d{2}[ T]([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/

  // Strict parsing on both branches. moment-jalaali returns undefined -- not an invalid
  // moment -- when a strict parse fails on a calendar-impossible date, so the result is
  // guarded before isValid() is reached.
  const formats = ['YYYY-MM-DDTHH:mm:ssZ', 'YYYY-MM-DDTHH:mm:ss.SSSZ']

  let instant
  if (isoShape.test(value)) {
    instant = momentJalaali(value, formats, true)
  } else if (legacyShape.test(value)) {
    // Normalise to a full timestamp before pinning the offset: the legacy layout appears with
    // and without seconds, and strict parsing rejects the one it was not given.
    const [date, clock] = value.replace('T', ' ').split(' ')
    const withSeconds = clock.length === 5 ? `${clock}:00` : clock
    instant = momentJalaali(`${date}T${withSeconds}+03:30`, formats, true)
  } else {
    return null
  }

  return instant && instant.isValid() ? instant.toDate() : null
}

/**
 * Shared body for the two above. `legacyZone` is what an UNLABELLED value means -- it is
 * never applied to an RFC 3339 value, which carries its own offset.
 */
function formatServerTime(
  value: string | null | undefined,
  legacyZone: '+03:30' | 'Z'
): string {
  if (!value) {
    return ''
  }

  // Shape is checked BEFORE parsing, and parsing is strict, because moment is lenient by
  // default and lenient is dangerous here: momentJalaali('not-a-date+03:30', ...) does not
  // fail, it returns YEAR 0330 and reports isValid() === true. A garbage timestamp would then
  // render as a confident, wrong date -- the same silently-plausible failure this change
  // exists to remove.
  let instant
  if (ISO_SESSION_TIME.test(value)) {
    if (!isValidISOSessionTime(value)) {
      return value
    }
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
      return value
    }
    instant = momentJalaali(parsed)
  } else if (LEGACY_SESSION_TIME.test(value)) {
    instant = momentJalaali(
      `${value}${legacyZone === 'Z' ? '+00:00' : legacyZone}`,
      'YYYY-MM-DD HH:mm:ssZ',
      true
    )
  } else {
    // Show the raw value rather than a fabricated date: an unparsed string is at least
    // evidence of what the server actually sent, and losing that evidence is what made the
    // original defect slow to pin down.
    return value
  }

  if (!instant.isValid()) {
    return value
  }

  // Never inherit a different locale selected by another Moment consumer.
  // Locale changes presentation only; instant parsing and viewer timezone stay intact.
  const local = instant.local().locale(lang)

  return lang === 'fa'
    ? '\u200E' + local.format('jYYYY-jMM-jDD HH:mm:ss')
    : local.format('YYYY-MM-DD HH:mm:ss')
}

/**
 * Render a session timestamp in the VIEWER's timezone.
 *
 * The BFF used to format these server-side in hard-coded Asia/Tehran and emit them with no
 * offset and no marker ("2026-08-23 20:14:42"). For a viewer anywhere else that is simply a
 * wrong time presented as if it were theirs, and because the string carries no zone there is
 * nothing on screen that would let them notice. It now sends RFC 3339 UTC instead.
 *
 * Both shapes are accepted on purpose, so the panel and the BFF do not have to deploy in
 * lockstep and neither order leaves a window of wrong times:
 *
 *   "2026-08-24T01:54:42Z"   an instant -- parse directly
 *   "2026-08-23 20:14:42"    legacy Tehran wall time -- pin it to +03:30 to get the instant
 *
 * +03:30 is a fixed offset rather than a zone lookup because Iran abolished DST in 2022, so
 * it is correct for anything a session table can contain. moment-timezone is not a
 * dependency here, and adding one to convert a format that is on its way out is not worth it.
 */
export function formatSessionTime(value: string | null | undefined): string {
  // Legacy session times were formatted server-side in hard-coded Asia/Tehran.
  return formatServerTime(value, '+03:30')
}

/**
 * Render a LOGIN-HISTORY timestamp in the viewer's timezone.
 *
 * Same shape as a session time and a DIFFERENT legacy zone, which is why this exists rather
 * than reusing formatSessionTime. Session times were formatted in hard-coded Asia/Tehran;
 * login histories came from FormatTimestamp, which renders in the BFF PROCESS's zone -- a
 * scratch image with no TZ, so UTC. The two produce byte-identical unlabelled strings that
 * mean instants 3h30m apart, so pinning login histories to +03:30 would move every legacy
 * value the wrong way by exactly the amount this fix exists to remove.
 *
 * The BFF now sends RFC 3339, which needs no assumption at all. The legacy branch is for the
 * deploy window only, and 'Z' is the correct reading of what the old formatter emitted.
 */
export function formatLoginAttemptTime(
  value: string | null | undefined
): string {
  return formatServerTime(value, 'Z')
}

export function gregorianToJalaaliWithHourMinuteAndSecond(
  gregorianDateWithTime: string
): string {
  if (!gregorianDateWithTime) {
    return ''
  }
  const formatted = momentJalaali(
    gregorianDateWithTime,
    'YYYY-MM-DD HH:mm:ss'
  ).format('jYYYY-jMM-jDD HH:mm:ss')

  // Same empty result as an empty input: both mean "no date to show". Callers already
  // render their own placeholder around this, so returning a symbol here would double up.
  if (jalaaliRenderFailed(formatted)) {
    return ''
  }

  // Add LTR mark to avoid RTL disruption
  return '\u200E' + formatted
}

export function gregorianToJalaaliWithHourMinuteAndSecondAndMilisecond(
  gregorianDateWithTime: string
): string {
  if (!gregorianDateWithTime) {
    return ''
  }
  const formatted = momentJalaali(
    gregorianDateWithTime,
    'YYYY-MM-DD HH:mm:ss.SSS'
  ).format('HH:mm:ss.SSS jYYYY-jMM-jDD')

  // Same zero-timestamp hazard as the seconds variant above; this one had no guard at all.
  return jalaaliRenderFailed(formatted) ? '' : formatted
}

const jalaaliTimeView = {
  basicView: 'jYYYY-jMM-jDD',
  dayView: 'jDD',
  monthView: 'jMMMM',
  yearView: 'jYYYY'
}

const gregorianTimeView = {
  basicView: 'YYYY-MM-DD',
  dayView: 'DD',
  monthView: 'MMM',
  yearView: 'YYYY'
}

export function getTimeView(type: DatePickerType) {
  return type === 'jalaali' ? jalaaliTimeView : gregorianTimeView
}

export function getStartDateFromDateObject(
  dateObject: DateObject,
  type: DatePickerType
) {
  if (type === 'jalaali') {
    return dateObject.gregorianStartDate
      ? gregorianToJalaali(dateObject.gregorianStartDate)
      : dateObject.jalaaliStartDate
  }

  return dateObject.jalaaliStartDate
    ? jalaaliToGregorian(dateObject.jalaaliStartDate)
    : dateObject.gregorianStartDate
}

export function getEndDateFromDateObject(
  dateObject: DateObject,
  type: DatePickerType
) {
  if (type === 'jalaali') {
    return dateObject.gregorianEndDate
      ? gregorianToJalaali(dateObject.gregorianEndDate)
      : dateObject.jalaaliEndDate
  }
  return dateObject.jalaaliEndDate
    ? jalaaliToGregorian(dateObject.jalaaliEndDate)
    : dateObject.gregorianEndDate
}

export const formatDateFullObject = (item: DateItem): string => {
  return momentJalaali(item.gregorianDate.date, 'YYYY-MM-DD').format(
    lang === 'fa' ? 'jDD jMMMM jYYYY' : 'MMM D, YYYY'
  )
}

export const formatDateRange = (
  startDate: string,
  endDate: string,
  l: 'fa' | 'en' = 'en'
): string => {
  const start = momentJalaali(startDate, 'YYYY-MM-DD')
  const end = momentJalaali(endDate, 'YYYY-MM-DD')

  // Check if the dates are in the same month and year
  const sameMonth = start.isSame(end, 'month')
  const sameYear = start.isSame(end, 'year')

  if (l === 'fa') {
    if (sameMonth && sameYear) {
      return `${start.format('jD')} تا ${end.format('jD jMMMM jYYYY')}`
    }
    return `${start.format('jD jMMMM')} تا ${end.format('jD jMMMM jYYYY')}`
  } else {
    if (sameMonth && sameYear) {
      return `${start.format('D')} to ${end.format('D MMM, YYYY')}`
    }
    return `${start.format('D MMM')} to ${end.format('D MMM, YYYY')}`
  }
}

export const getTodayDate = (): string => {
  return momentJalaali().format('YYYY-MM-DD')
}

export const addDaysToDate = (dateString: string, days: number): string => {
  return momentJalaali(dateString, 'YYYY-MM-DD')
    .add(days, 'days')
    .format('YYYY-MM-DD')
}

export const isWeekend = (item: DateItem): boolean => {
  return ['Thu', 'Fri', 'پنج‌شنبه', 'جمعه'].includes(item.gregorianDate.weekDay)
}

export function DeleteExtraDetailsFromDate(datetimeStr: string): string {
  return datetimeStr.slice(0, 10)
}

export type DatePickerSelectionType = 'TimeInterval' | 'CustomDays'

export const isValidDateFormat = (date: string | null): boolean => {
  const dateRegex = /^(?:(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))?$/
  return dateRegex.test(date ?? '')
}

export const convertGregorianToJalali = (date: string): string => {
  if (!date) return ''

  const jalaliDate = momentJalaali(date, 'YYYY-MM-DD')

  if (!jalaliDate.isValid()) {
    return ''
  }

  return jalaliDate.format('jYYYY-jMM-jDD')
}
export const formatRange = (from: string, to: string) => {
  const startDate = momentJalaali(from, 'YYYY-MM-DD')
  const endDate = momentJalaali(to, 'YYYY-MM-DD')

  // Choose format based on language
  const monthDayFormat = lang === 'fa' ? 'jD jMMM' : 'D MMM'

  // Format dates accordingly
  const formattedFrom = startDate.format(monthDayFormat)
  const formattedTo = endDate.format(monthDayFormat)

  return `${formattedFrom} ${getTranslate('to')} ${formattedTo}`
}

export const formatShortDateWithWeekDay = (
  date: string,
  locale: 'fa' | 'en' = 'en'
): string => {
  const format = locale === 'fa' ? 'ddd، jD jMMMM' : 'ddd, MMMM D'
  return momentJalaali(date, 'YYYY-MM-DD').format(format)
}

export const formatTimeLeft = (seconds: number) => {
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  return `${hrs.toString().padStart(2, '0')}:${mins
    .toString()
    .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

// export function toISO8601(date: string, time: string): string {
//   // Ensure time has seconds
//   const timeWithSeconds = time.length === 5 ? `${time}:00` : time

//   // Combine into ISO format
//   return `${date}T${timeWithSeconds}Z`
// }

// Function to split datetime string
export function splitDateTime(datetime: string): {
  date: string
  time: string
} {
  if (!datetime) {
    return { date: '2025-09-28', time: '10:00' }
  }

  // Accept both separators. This used to split on a space only, which was correct until the
  // BFF began sending RFC 3339 ("2026-08-25T10:30:00Z") to fix a timezone bug. After that the
  // split produced a single element, `time` was undefined, and the slice below threw during
  // BookerDetailsForm's render -- a blank page, from a value the server had sent correctly.
  //
  // date.ts already normalises `T` this way one function above; this one was missed.
  const [date, clock] = datetime.replace('T', ' ').split(' ')

  // A value with no time part is not a reason to blank the page. This runs during render
  // against a server field, so it degrades to the default clock rather than throwing.
  const shortTime = clock ? clock.slice(0, 5) : '10:00' // keeps only "HH:mm"
  return { date, time: shortTime }
}

// Function to combine date and time back into datetime
export function combineDateTime(date: string, time: string): string {
  return `${date} ${time}`
}

export function getFormattedNow(l: string): string {
  if (l === 'fa') {
    // Persian (Jalali)
    return momentJalaali().format('jYYYY-jMM-jDD HH:mm:ss')
  }

  // English (Gregorian)
  return momentJalaali().format('YYYY-MM-DD HH:mm:ss')
}

export const formatTime = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const paddedMinutes = String(minutes).padStart(2, '0')
  const paddedSeconds = String(seconds).padStart(2, '0')

  return hours > 0
    ? `${hours}′ : ${paddedMinutes}′ : ${paddedSeconds}″`
    : seconds > 0
    ? `${minutes}′ : ${paddedSeconds}″`
    : minutes == 0
    ? `${getTranslate('Expired')}`
    : `${minutes}′ : ${paddedSeconds}″`
}
